import React, { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import UiIcon from '../icons/UiIcon';

/**
 * The chrome around every Add/Edit form: title bar, the four window controls,
 * and a scrolling body. Presentation only — it owns no form state and never
 * touches the API; `<FormWindowProvider>` owns which window is where.
 *
 * Two deliberate behaviours, both about not destroying typed work:
 *   • ESC and a backdrop click MINIMIZE, they do not close. A half-typed lead
 *     parked in the dock is recoverable; the same lead wiped by a stray click
 *     outside the panel is not, and that is the single most common way a CRM
 *     teaches its users to stop entering data.
 *   • Only the explicit × discards, and the provider confirms first when the
 *     form reports itself dirty.
 *
 * `hidden` keeps a window MOUNTED but out of view — that is what lets a draft
 * survive being minimised, and survive the user navigating to another page.
 */
export default function FormWindowShell({
  title,
  width = 'md',
  maximized = false,
  hidden = false,
  canOpenFullPage = false,
  onMinimize,
  onToggleMaximize,
  onOpenFullPage,
  onClose,
  children,
}) {
  const { t } = useTranslation();
  const titleId = useId();
  const panelRef = useRef(null);

  // ESC minimises the visible window. Bound per-window but guarded on `hidden`
  // so the parked ones in the dock never answer the keystroke.
  useEffect(() => {
    if (hidden) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      onMinimize();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [hidden, onMinimize]);

  // Land the caret in the first field rather than on the close button — this
  // is a form the user opened to type into. Deferred a frame because the body
  // arrives through <Suspense> and may not be mounted on the first paint.
  useEffect(() => {
    if (hidden) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const field = panel.querySelector(
        '.fw-body input:not([type="hidden"]):not([disabled]), .fw-body select:not([disabled]), .fw-body textarea:not([disabled])',
      );
      (field ?? panel).focus?.();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [hidden]);

  return (
    <div
      className={`fw-overlay${maximized ? ' fw-overlay-max' : ''}`}
      role="presentation"
      style={hidden ? { display: 'none' } : undefined}
      onMouseDown={(event) => { if (event.target === event.currentTarget) onMinimize(); }}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        className={`fw-panel fw-form-${width}${maximized ? ' fw-panel-max' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <div className="fw-head">
          <h2 id={titleId} className="fw-title">{title}</h2>
          <div className="fw-controls">
            <button
              type="button"
              className="fw-ctl"
              aria-label={t('formWindow.minimize')}
              title={t('formWindow.minimize')}
              onClick={onMinimize}
            >
              <UiIcon name="minimize" className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className="fw-ctl"
              aria-label={maximized ? t('formWindow.restore') : t('formWindow.maximize')}
              title={maximized ? t('formWindow.restore') : t('formWindow.maximize')}
              aria-pressed={maximized}
              onClick={onToggleMaximize}
            >
              <UiIcon name={maximized ? 'restore' : 'maximize'} className="h-3.5 w-3.5" />
            </button>
            {canOpenFullPage && (
              <button
                type="button"
                className="fw-ctl"
                aria-label={t('formWindow.fullPage')}
                title={t('formWindow.fullPage')}
                onClick={onOpenFullPage}
              >
                <UiIcon name="expand" className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              type="button"
              className="fw-ctl fw-ctl-close"
              aria-label={t('formWindow.close')}
              title={t('formWindow.close')}
              onClick={onClose}
            >
              <UiIcon name="close" className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        <div className="fw-body">{children}</div>
      </div>
    </div>
  );
}
